import React, { useCallback, useEffect, useState } from 'react';
import MainLayout from '../../layouts/MainLayout';
import {
  Card,
  Table,
  Button,
  Space,
  message,
  Modal,
  Form,
  Input,
  Select,
  Popconfirm,
  Tag,
  Tabs,
  Switch,
  Divider,
  Empty,
  Typography,
  Badge,
} from 'antd';
import {
  PlusOutlined,
  ReloadOutlined,
  DeleteOutlined,
  EditOutlined,
  BookOutlined,
  FolderOutlined,
  EyeOutlined,
  FileTextOutlined,
  SearchOutlined,
  BarsOutlined,
  MessageOutlined,
  SettingOutlined,
} from '@ant-design/icons';
import {
  knowledgeCategoriesApi,
  knowledgeBaseApi,
  knowledgeRagApi,
  show422Error,
  ValidationError,
} from '../../utils/api';
import { TableSkeleton } from '../../components/SkeletonLoaders';
import PermissionGate from '../../components/PermissionGate';
import { usePermissions } from '../../contexts/PermissionsContext';
import KnowledgeRagSettings from '../../components/knowledge-base/KnowledgeRagSettings';
import KnowledgeTestChat from '../../components/knowledge-base/KnowledgeTestChat';
import EmbeddingStatusNotice from '../../components/knowledge-base/EmbeddingStatusNotice';
import { PANEL_PRIMARY_BTN_CLASS } from '../../constants/ui';

const { Paragraph } = Typography;

const KnowledgeBase = () => {
  const { hasPermission, isAdmin, isSuperAdmin } = usePermissions();
  const canManage = Boolean(isAdmin || isSuperAdmin || hasPermission('manage_knowledgebase'));
  const [activeTab, setActiveTab] = useState('knowledges');

  // --- State: Categories ---
  const [categoriesLoading, setCategoriesLoading] = useState(false);
  const [categories, setCategories] = useState([]);
  const [categoryModalOpen, setCategoryModalOpen] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [categoryForm] = Form.useForm();
  const [categorySubmitting, setCategorySubmitting] = useState(false);
  const [deletingCategoryId, setDeletingCategoryId] = useState(null);

  // --- State: Knowledges ---
  const [knowledgesLoading, setKnowledgesLoading] = useState(false);
  const [knowledges, setKnowledges] = useState([]);
  const [knowledgeModalOpen, setKnowledgeModalOpen] = useState(false);
  const [editingKnowledge, setEditingKnowledge] = useState(null);
  const [knowledgeForm] = Form.useForm();
  const [knowledgeSubmitting, setKnowledgeSubmitting] = useState(false);
  const [deletingKnowledgeId, setDeletingKnowledgeId] = useState(null);

  // --- State: View Knowledge Detail Modal ---
  const [viewKnowledge, setViewKnowledge] = useState(null);
  const [viewModalOpen, setViewModalOpen] = useState(false);

  // --- Filters ---
  const [selectedCategoryFilter, setSelectedCategoryFilter] = useState(null);
  const [searchKeyword, setSearchKeyword] = useState('');

  // --- State: دستیار هوشمند (به‌روزرسانی دستی توسط مدیر) ---
  const [ragStatus, setRagStatus] = useState(null);
  const [embeddingRunning, setEmbeddingRunning] = useState(false);

  // 1. دریافت لیست دسته‌بندی‌ها
  const fetchCategories = useCallback(async () => {
    setCategoriesLoading(true);
    try {
      const res = await knowledgeCategoriesApi.getAll();
      const payload = res?.data ?? [];
      setCategories(Array.isArray(payload) ? payload : payload.data ?? []);
    } catch (error) {
      console.error('خطا در دریافت دسته‌بندی‌های پایگاه دانش:', error);
      message.error(error?.message || 'خطا در دریافت دسته‌بندی‌ها');
    } finally {
      setCategoriesLoading(false);
    }
  }, []);

  // 2. دریافت لیست دانش‌ها
  const fetchKnowledges = useCallback(async () => {
    setKnowledgesLoading(true);
    try {
      const params = {};
      if (selectedCategoryFilter) params.category_id = selectedCategoryFilter;
      if (searchKeyword.trim()) params.search = searchKeyword.trim();

      const res = await knowledgeBaseApi.getAll(params);
      const payload = res?.data ?? [];
      setKnowledges(Array.isArray(payload) ? payload : payload.data ?? []);
    } catch (error) {
      console.error('خطا در دریافت دانش‌ها:', error);
      message.error(error?.message || 'خطا در دریافت دانش‌ها');
    } finally {
      setKnowledgesLoading(false);
    }
  }, [selectedCategoryFilter, searchKeyword]);

  useEffect(() => {
    fetchCategories();
  }, [fetchCategories]);

  useEffect(() => {
    fetchKnowledges();
  }, [fetchKnowledges]);

  const fetchRagStatus = useCallback(async () => {
    try {
      const res = await knowledgeRagApi.getStatus();
      setRagStatus(res?.data ?? null);
    } catch {
      setRagStatus(null);
    }
  }, []);

  useEffect(() => {
    fetchRagStatus();
  }, [fetchRagStatus]);

  // به‌روزرسانی دستی دستیار: همه بخش‌ها در یک درخواست GapGPT
  const runEmbedding = useCallback(async () => {
    setEmbeddingRunning(true);
    try {
      const res = await knowledgeRagApi.sync();
      message.success(res?.message || 'دستیار به‌روز شد.');
      setRagStatus(res?.data?.status ?? null);
    } catch (error) {
      message.error(error?.message || 'خطا در به‌روزرسانی دستیار');
      fetchRagStatus();
    } finally {
      setEmbeddingRunning(false);
    }
  }, [fetchRagStatus]);

  // ==================== عملیات دسته‌بندی‌ها ====================

  const openAddCategoryModal = () => {
    setEditingCategory(null);
    categoryForm.resetFields();
    categoryForm.setFieldsValue({
      is_active: true,
      order: 0,
    });
    setCategoryModalOpen(true);
  };

  const openEditCategoryModal = (record) => {
    setEditingCategory(record);
    categoryForm.resetFields();
    categoryForm.setFieldsValue({
      name: record.name,
      description: record.description,
      is_active: Boolean(record.is_active),
      order: record.order || 0,
    });
    setCategoryModalOpen(true);
  };

  const submitCategory = async () => {
    try {
      const values = await categoryForm.validateFields();
      setCategorySubmitting(true);

      const payload = {
        name: values.name.trim(),
        description: values.description ? values.description.trim() : null,
        is_active: values.is_active,
        order: values.order || 0,
      };

      if (editingCategory) {
        await knowledgeCategoriesApi.update(editingCategory.id, payload);
        message.success('دسته‌بندی با موفقیت به‌روزرسانی شد');
      } else {
        await knowledgeCategoriesApi.create(payload);
        message.success('دسته‌بندی جدید با موفقیت اضافه شد');
      }

      setCategoryModalOpen(false);
      await fetchCategories();
      await fetchKnowledges();
      fetchRagStatus();
    } catch (error) {
      if (error?.errorFields) return;
      console.error('خطا در ذخیره دسته‌بندی:', error);
      if (error instanceof ValidationError) {
        show422Error(error, categoryForm);
      } else {
        message.error(error?.message || 'خطا در ذخیره دسته‌بندی');
      }
    } finally {
      setCategorySubmitting(false);
    }
  };

  const deleteCategory = async (record) => {
    setDeletingCategoryId(record.id);
    try {
      await knowledgeCategoriesApi.delete(record.id);
      message.success('دسته‌بندی با موفقیت حذف شد');
      if (selectedCategoryFilter === record.id) {
        setSelectedCategoryFilter(null);
      }
      await fetchCategories();
      await fetchKnowledges();
      fetchRagStatus();
    } catch (error) {
      console.error('خطا در حذف دسته‌بندی:', error);
      message.error(error?.message || 'خطا در حذف دسته‌بندی');
    } finally {
      setDeletingCategoryId(null);
    }
  };

  // ==================== عملیات دانش‌ها و جعبه‌های چندبخشی ====================

  const openAddKnowledgeModal = () => {
    if (categories.length === 0) {
      message.warning('ابتدا باید حداقل یک دسته‌بندی ایجاد نمایید');
      setActiveTab('categories');
      return;
    }

    setEditingKnowledge(null);
    knowledgeForm.resetFields();
    knowledgeForm.setFieldsValue({
      category_id: selectedCategoryFilter || categories[0]?.id,
      is_active: true,
      order: 0,
      parts: [''],
    });
    setKnowledgeModalOpen(true);
  };

  const openEditKnowledgeModal = (record) => {
    setEditingKnowledge(record);
    knowledgeForm.resetFields();

    const partsContent = (record.parts || []).map((p) => p.content);
    knowledgeForm.setFieldsValue({
      category_id: record.category_id,
      title: record.title,
      description: record.description,
      is_active: Boolean(record.is_active),
      order: record.order || 0,
      parts: partsContent.length > 0 ? partsContent : [''],
    });
    setKnowledgeModalOpen(true);
  };

  const submitKnowledge = async () => {
    try {
      const values = await knowledgeForm.validateFields();
      const validParts = (values.parts || [])
        .map((p) => (typeof p === 'string' ? p.trim() : (p?.content || '').trim()))
        .filter(Boolean);

      if (validParts.length === 0) {
        message.error('حداقل یک بخش / جعبه متنی از دانش باید پر شود');
        return;
      }

      setKnowledgeSubmitting(true);

      const payload = {
        category_id: values.category_id,
        title: values.title.trim(),
        description: values.description ? values.description.trim() : null,
        is_active: values.is_active,
        order: values.order || 0,
        parts: validParts,
      };

      if (editingKnowledge) {
        await knowledgeBaseApi.update(editingKnowledge.id, payload);
        message.success('دانش با موفقیت به‌روزرسانی شد');
      } else {
        await knowledgeBaseApi.create(payload);
        message.success('دانش جدید با موفقیت اضافه شد');
      }

      setKnowledgeModalOpen(false);
      await fetchKnowledges();
      await fetchCategories();
      fetchRagStatus();
    } catch (error) {
      if (error?.errorFields) return;
      console.error('خطا در ذخیره دانش:', error);
      if (error instanceof ValidationError) {
        show422Error(error, knowledgeForm);
      } else {
        message.error(error?.message || 'خطا در ذخیره دانش');
      }
    } finally {
      setKnowledgeSubmitting(false);
    }
  };

  const deleteKnowledge = async (record) => {
    setDeletingKnowledgeId(record.id);
    try {
      await knowledgeBaseApi.delete(record.id);
      message.success('دانش با موفقیت حذف شد');
      await fetchKnowledges();
      await fetchCategories();
      fetchRagStatus();
    } catch (error) {
      console.error('خطا در حذف دانش:', error);
      message.error(error?.message || 'خطا در حذف دانش');
    } finally {
      setDeletingKnowledgeId(null);
    }
  };

  const openViewDetail = (record) => {
    setViewKnowledge(record);
    setViewModalOpen(true);
  };

  // ==================== ستون‌های جدول دسته‌بندی‌ها ====================
  const categoryColumns = [
    {
      title: 'نام دسته‌بندی',
      dataIndex: 'name',
      key: 'name',
      render: (name, record) => (
        <div className="flex items-center gap-2">
          <FolderOutlined className="text-teal-600 text-base" />
          <span className="font-semibold text-gray-800 dark:text-gray-200">{name}</span>
          {!record.is_active && <Tag color="default">غیرفعال</Tag>}
        </div>
      ),
    },
    {
      title: 'توضیحات کوتاه (حداکثر ۲۵۵ کاراکتر)',
      dataIndex: 'description',
      key: 'description',
      ellipsis: true,
      render: (desc) => (
        <span className="text-gray-600 dark:text-gray-400 text-sm">
          {desc || <span className="text-gray-400 italic">بدون توضیح</span>}
        </span>
      ),
    },
    {
      title: 'تعداد دانش‌ها',
      key: 'knowledges_count',
      width: 140,
      align: 'center',
      render: (_, record) => (
        <Tag color="cyan" className="font-mono text-xs">
          {record.knowledges_count || 0} مورد
        </Tag>
      ),
    },
    {
      title: 'تعداد بخش‌ها',
      key: 'parts_count',
      width: 160,
      align: 'center',
      render: (_, record) => (
        <Tag color="purple" className="font-mono text-xs">
          {record.parts_count || 0} بخش
        </Tag>
      ),
    },
    {
      title: 'وضعیت',
      dataIndex: 'is_active',
      key: 'is_active',
      width: 110,
      align: 'center',
      render: (isActive) =>
        isActive ? (
          <Tag color="success">فعال</Tag>
        ) : (
          <Tag color="error">غیرفعال</Tag>
        ),
    },
    {
      title: 'عملیات',
      key: 'actions',
      width: 120,
      align: 'center',
      render: (_, record) => (
        <Space size="small">
          <PermissionGate module="knowledgebase" permission="manage_knowledgebase">
            <Button
              type="text"
              icon={<EditOutlined className="text-blue-600" />}
              onClick={() => openEditCategoryModal(record)}
              title="ویرایش دسته‌بندی"
            />
            <Popconfirm
              title="آیا از حذف این دسته‌بندی و تمام دانش‌های درون آن اطمینان دارید؟"
              onConfirm={() => deleteCategory(record)}
              okText="بله، حذف کن"
              cancelText="انصراف"
              placement="topRight"
            >
              <Button
                type="text"
                danger
                icon={<DeleteOutlined />}
                loading={deletingCategoryId === record.id}
                title="حذف دسته‌بندی"
              />
            </Popconfirm>
          </PermissionGate>
        </Space>
      ),
    },
  ];

  // ==================== ستون‌های جدول دانش‌ها ====================
  const knowledgeColumns = [
    {
      title: 'عنوان',
      key: 'title',
      render: (_, record) => (
        <div className="flex flex-col gap-1">
          <div className="flex items-center gap-2">
            <BookOutlined className="text-indigo-600 text-sm" />
            <span className="font-semibold text-gray-800 dark:text-gray-200">{record.title}</span>
            {!record.is_active && <Tag color="default">غیرفعال</Tag>}
          </div>
          <div className="flex items-center gap-2 mt-1">
            <Tag color="blue" className="text-xs">
              <FolderOutlined className="ml-1" />
              {record.category?.name || 'بدون دسته‌بندی'}
            </Tag>
            {record.description && (
              <span className="text-gray-400 text-xs truncate max-w-md">
                {record.description}
              </span>
            )}
          </div>
        </div>
      ),
    },
    {
      title: 'محتوا',
      key: 'parts',
      width: 250,
      render: (_, record) => {
        const partsCount = (record.parts || []).length;
        const firstPart = record.parts?.[0]?.content || '';
        return (
          <div className="flex flex-col gap-1">
            <div className="flex items-center gap-1">
              <Tag color="geekblue" className="font-semibold text-xs">
                {partsCount} بخش
              </Tag>
            </div>
            {firstPart && (
              <span className="text-gray-500 text-xs italic line-clamp-1" title={firstPart}>
                {firstPart.substring(0, 50)}...
              </span>
            )}
          </div>
        );
      },
    },
    {
      title: 'وضعیت',
      dataIndex: 'is_active',
      key: 'is_active',
      width: 100,
      align: 'center',
      render: (isActive) =>
        isActive ? (
          <Tag color="success">فعال</Tag>
        ) : (
          <Tag color="error">غیرفعال</Tag>
        ),
    },
    {
      title: 'عملیات',
      key: 'actions',
      width: 150,
      align: 'center',
      render: (_, record) => (
        <Space size="small">
          <Button
            type="text"
            icon={<EyeOutlined className="text-teal-600" />}
            onClick={() => openViewDetail(record)}
            title="مشاهده جزئیات و بخش‌ها"
          />
          <PermissionGate module="knowledgebase" permission="manage_knowledgebase">
            <Button
              type="text"
              icon={<EditOutlined className="text-blue-600" />}
              onClick={() => openEditKnowledgeModal(record)}
              title="ویرایش دانش"
            />
            <Popconfirm
              title="آیا از حذف این دانش اطمینان دارید؟"
              onConfirm={() => deleteKnowledge(record)}
              okText="بله، حذف کن"
              cancelText="انصراف"
              placement="topRight"
            >
              <Button
                type="text"
                danger
                icon={<DeleteOutlined />}
                loading={deletingKnowledgeId === record.id}
                title="حذف دانش"
              />
            </Popconfirm>
          </PermissionGate>
        </Space>
      ),
    },
  ];

  return (
    <MainLayout
      title="پایگاه دانش"
      activeMenu="/knowledge-base"
      breadcrumbs={[
        { title: 'پایگاه دانش' },
      ]}
    >
      <div className="w-full space-y-4">
        {/* هدر */}
        <Card
          bordered={false}
          className="shadow-sm rounded-lg bg-gradient-to-l from-indigo-50/50 to-teal-50/50 dark:from-gray-800 dark:to-gray-800"
          styles={{ body: { padding: '1.25rem' } }}
        >
          <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
            <div className="space-y-1">
              <div className="flex items-center gap-2">
                <BookOutlined className="text-indigo-600 text-xl" />
                <h2 className="text-lg font-bold text-gray-800 dark:text-gray-100 m-0">
                  پایگاه دانش
                </h2>
              </div>
              <p className="text-xs text-gray-500 dark:text-gray-400 m-0 leading-relaxed max-w-2xl">
                اطلاعات و پاسخ‌های سازمان را اینجا ثبت کنید تا همکاران و دستیار هوشمند از آن استفاده کنند.
              </p>
            </div>

            <div className="flex items-center gap-3 flex-wrap">
              <div className="bg-white dark:bg-gray-700 px-3 py-2 rounded-lg border border-gray-100 dark:border-gray-600 shadow-xs flex items-center gap-2">
                <FolderOutlined className="text-teal-600" />
                <div className="text-xs">
                  <span className="text-gray-400 block">دسته‌بندی‌ها</span>
                  <span className="font-bold text-gray-700 dark:text-gray-200 font-mono">
                    {categories.length}
                  </span>
                </div>
              </div>

              <div className="bg-white dark:bg-gray-700 px-3 py-2 rounded-lg border border-gray-100 dark:border-gray-600 shadow-xs flex items-center gap-2">
                <FileTextOutlined className="text-indigo-600" />
                <div className="text-xs">
                  <span className="text-gray-400 block">دانش‌ها</span>
                  <span className="font-bold text-gray-700 dark:text-gray-200 font-mono">
                    {knowledges.length}
                  </span>
                </div>
              </div>

            </div>
          </div>
        </Card>

        {/* تب‌های ماژول */}
        <Card
          bordered={false}
          className="shadow-sm rounded-lg"
          styles={{ body: { padding: '1rem 1.5rem 1.5rem' } }}
        >
          <Tabs
            activeKey={activeTab}
            onChange={setActiveTab}
            items={[
              {
                key: 'knowledges',
                label: (
                  <span className="flex items-center gap-2">
                    <BookOutlined />
                    <span>دانش‌ها</span>
                    <Badge count={knowledges.length} overflowCount={999} style={{ backgroundColor: '#4f46e5' }} />
                  </span>
                ),
                children: (
                  <div className="space-y-4 pt-2">
                    <div className="flex flex-col md:flex-row items-stretch md:items-center justify-between gap-3">
                      <div className="flex flex-wrap items-center gap-2 flex-1">
                        <Input
                          placeholder="جستجو در عنوان یا متن بخش‌ها..."
                          prefix={<SearchOutlined className="text-gray-400" />}
                          value={searchKeyword}
                          onChange={(e) => setSearchKeyword(e.target.value)}
                          allowClear
                          className="w-full sm:w-64 custom-font"
                        />

                        <Select
                          placeholder="فیلتر بر اساس دسته‌بندی"
                          value={selectedCategoryFilter}
                          onChange={setSelectedCategoryFilter}
                          allowClear
                          className="w-full sm:w-56 custom-font"
                          options={[
                            { value: null, label: 'همه دسته‌بندی‌ها' },
                            ...categories.map((c) => ({
                              value: c.id,
                              label: `${c.name} (${c.knowledges_count || 0})`,
                            })),
                          ]}
                        />

                        <Button
                          icon={<ReloadOutlined />}
                          onClick={() => {
                            fetchKnowledges();
                            fetchCategories();
                            fetchRagStatus();
                          }}
                          title="تازه سازی لیست"
                        />
                      </div>

                      <PermissionGate module="knowledgebase" permission="manage_knowledgebase">
                        <Button
                          type="primary"
                          icon={<PlusOutlined />}
                          onClick={openAddKnowledgeModal}
                          className={PANEL_PRIMARY_BTN_CLASS}
                        >
                          افزودن دانش جدید
                        </Button>
                      </PermissionGate>
                    </div>

                    {canManage && ragStatus?.total_parts > 0 && (
                      <EmbeddingStatusNotice status={ragStatus} running={embeddingRunning} onRun={runEmbedding} />
                    )}

                    {knowledgesLoading ? (
                      <TableSkeleton rows={5} columns={4} />
                    ) : (
                      <Table
                        columns={knowledgeColumns}
                        dataSource={knowledges}
                        rowKey="id"
                        pagination={{
                          pageSize: 10,
                          showTotal: (total) => `مجموع: ${total} مورد`,
                        }}
                        locale={{
                          emptyText: (
                            <Empty
                              description={
                                categories.length === 0
                                  ? 'هنوز دسته‌بندی ایجاد نشده است. ابتدا یک دسته‌بندی بسازید.'
                                  : 'هیچ دانشی ثبت نشده است.'
                              }
                            >
                              {categories.length === 0 && (
                                <Button
                                  type="primary"
                                  onClick={() => setActiveTab('categories')}
                                  className={PANEL_PRIMARY_BTN_CLASS}
                                >
                                  رفتن به بخش دسته‌بندی‌ها
                                </Button>
                              )}
                            </Empty>
                          ),
                        }}
                      />
                    )}
                  </div>
                ),
              },
              {
                key: 'categories',
                label: (
                  <span className="flex items-center gap-2">
                    <FolderOutlined />
                    <span>دسته‌بندی‌ها</span>
                    <Badge count={categories.length} overflowCount={999} style={{ backgroundColor: '#0d9488' }} />
                  </span>
                ),
                children: (
                  <div className="space-y-4 pt-2">
                    <div className="flex items-center justify-between gap-3">
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-gray-500">
                          برای دسته‌بندی موضوعات؛ مثل پشتیبانی، فروش یا قوانین داخلی.
                        </span>
                        <Button
                          icon={<ReloadOutlined />}
                          size="small"
                          onClick={fetchCategories}
                          title="تازه سازی لیست"
                        />
                      </div>

                      <PermissionGate module="knowledgebase" permission="manage_knowledgebase">
                        <Button
                          type="primary"
                          icon={<PlusOutlined />}
                          onClick={openAddCategoryModal}
                          className={PANEL_PRIMARY_BTN_CLASS}
                        >
                          افزودن دسته‌بندی
                        </Button>
                      </PermissionGate>
                    </div>

                    {categoriesLoading ? (
                      <TableSkeleton rows={4} columns={5} />
                    ) : (
                      <Table
                        columns={categoryColumns}
                        dataSource={categories}
                        rowKey="id"
                        pagination={{ pageSize: 10 }}
                        locale={{
                          emptyText: <Empty description="هنوز هیچ دسته‌بندی ثبت نشده است." />,
                        }}
                      />
                    )}
                  </div>
                ),
              },
              {
                key: 'test-chat',
                label: (
                  <span className="flex items-center gap-2">
                    <MessageOutlined />
                    <span>پرسش از دستیار</span>
                  </span>
                ),
                children: (
                  <div className="space-y-3 pt-2">
                    {canManage && (
                      <EmbeddingStatusNotice status={ragStatus} running={embeddingRunning} onRun={runEmbedding} />
                    )}
                    <KnowledgeTestChat />
                  </div>
                ),
              },
              ...(canManage ? [{
                key: 'rag-settings',
                label: (
                  <span className="flex items-center gap-2">
                    <SettingOutlined />
                    <span>تنظیمات دستیار</span>
                  </span>
                ),
                children: (
                  <KnowledgeRagSettings
                    status={ragStatus}
                    onRefreshStatus={fetchRagStatus}
                    onRunEmbedding={runEmbedding}
                    embeddingRunning={embeddingRunning}
                  />
                ),
              }] : []),
            ]}
          />
        </Card>

        {/* مدال افزودن/ویرایش دسته‌بندی */}
        <Modal
          title={editingCategory ? 'ویرایش دسته‌بندی پایگاه دانش' : 'افزودن دسته‌بندی جدید'}
          open={categoryModalOpen}
          onOk={submitCategory}
          onCancel={() => setCategoryModalOpen(false)}
          confirmLoading={categorySubmitting}
          okText="ذخیره دسته‌بندی"
          cancelText="انصراف"
          okButtonProps={{ className: PANEL_PRIMARY_BTN_CLASS }}
          destroyOnClose
        >
          <Form form={categoryForm} layout="vertical" className="mt-4 custom-font">
            <Form.Item
              name="name"
              label="نام دسته‌بندی"
              rules={[{ required: true, message: 'لطفاً نام دسته‌بندی را وارد کنید' }]}
            >
              <Input
                placeholder="مثال: راهنمایی مشتری برای خرید محصول"
                maxLength={255}
                className="custom-font"
              />
            </Form.Item>

            <Form.Item
              name="description"
              label="توضیحات کوتاه (حداکثر ۲۵۵ کاراکتر)"
              rules={[{ max: 255, message: 'توضیحات نمی‌تواند بیشتر از ۲۵۵ کاراکتر باشد' }]}
              extra="یک توضیح کوتاه و مختصر جهت آشنایی با هدف این دسته‌بندی بنویسید (حداکثر ۲۵۵ کاراکتر)."
            >
              <Input.TextArea
                rows={3}
                maxLength={255}
                showCount
                placeholder="مثال: استراتژی‌های هدایت مشتری به سمت خرید و پاسخ به تردیدها درباره قیمت و مقایسه با رقبا"
                className="custom-font"
              />
            </Form.Item>

            <Form.Item
              name="is_active"
              label="وضعیت فعالیت"
              valuePropName="checked"
            >
              <Switch checkedChildren="فعال" unCheckedChildren="غیرفعال" />
            </Form.Item>
          </Form>
        </Modal>

        {/* مدال افزودن/ویرایش دانش (با جعبه‌های چندبخشی) */}
        <Modal
          title={
            <div className="flex items-center gap-2">
              <BookOutlined className="text-indigo-600" />
              <span>{editingKnowledge ? 'ویرایش دانش و بخش‌های متنی' : 'افزودن دانش جدید (جعبه‌های چندبخشی)'}</span>
            </div>
          }
          open={knowledgeModalOpen}
          onOk={submitKnowledge}
          onCancel={() => setKnowledgeModalOpen(false)}
          confirmLoading={knowledgeSubmitting}
          okText="ذخیره دانش"
          cancelText="انصراف"
          okButtonProps={{ className: PANEL_PRIMARY_BTN_CLASS }}
          width={760}
          destroyOnClose
        >
          <Form form={knowledgeForm} layout="vertical" className="mt-4 custom-font">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Form.Item
                name="category_id"
                label="دسته‌بندی مربوطه"
                rules={[{ required: true, message: 'انتخاب دسته‌بندی الزامی است' }]}
              >
                <Select
                  placeholder="دسته‌بندی را انتخاب کنید"
                  options={categories.map((c) => ({
                    value: c.id,
                    label: `${c.name}${c.description ? ` (${c.description.substring(0, 30)}...)` : ''}`,
                  }))}
                  className="custom-font"
                />
              </Form.Item>

              <Form.Item
                name="title"
                label="عنوان دانش"
                rules={[{ required: true, message: 'لطفاً عنوان دانش را وارد کنید' }]}
              >
                <Input
                  placeholder="مثال: پاسخ به دغدغه قیمت بالا یا درخواست تخفیف"
                  maxLength={255}
                  className="custom-font"
                />
              </Form.Item>
            </div>

            <Form.Item
              name="description"
              label="یادداشت / خلاصه کوتاه (اختیاری)"
            >
              <Input.TextArea
                rows={2}
                placeholder="توضیح تکمیلی یا زمینه این دانش..."
                className="custom-font"
              />
            </Form.Item>

            <Divider className="my-3">
              <span className="text-xs text-gray-500 flex items-center gap-1">
                <BarsOutlined />
                متن دانش
              </span>
            </Divider>

            <p className="text-xs text-gray-500 dark:text-gray-400 mb-3">
              متن را در چند بخش کوتاه بنویسید؛ هر بخش درباره یک موضوع.
            </p>

            <Form.List name="parts">
              {(fields, { add, remove }) => (
                <div className="space-y-3">
                  {fields.map((field, index) => (
                    <Card
                      key={field.key}
                      size="small"
                      className="border border-gray-200 dark:border-gray-700 bg-gray-50/40 dark:bg-gray-800/40 rounded-lg"
                      styles={{ body: { padding: '0.75rem' } }}
                      title={
                        <div className="flex items-center justify-between">
                          <span className="font-semibold text-xs text-indigo-700 dark:text-indigo-400 flex items-center gap-1.5">
                            <Tag color="indigo" className="m-0 font-mono text-[11px]">
                              بخش {index + 1}
                            </Tag>
                          </span>
                          {fields.length > 1 && (
                            <Button
                              type="text"
                              danger
                              size="small"
                              icon={<DeleteOutlined />}
                              onClick={() => remove(field.name)}
                              title="حذف این بخش"
                            >
                              حذف بخش
                            </Button>
                          )}
                        </div>
                      }
                    >
                      <Form.Item
                        {...field}
                        rules={[
                          {
                            required: true,
                            message: `لطفاً متن بخش ${index + 1} را وارد کنید`,
                          },
                        ]}
                        className="m-0"
                      >
                        <Input.TextArea
                          rows={4}
                          placeholder={`مثال: بخش ${index + 1} — در صورت اعتراض مشتری به قیمت بالا:\n۱. با همدلی دغدغه مشتری را بشنوید و نیاز او را تایید نمایید.\n۲. روی خدمات پس از فروش رایگان و کیفیت تضمین‌شده تاکید کنید.\n۳. پیشنهاد پرداخت اقساطی یا تخفیف فصلی ۵٪ را ارائه دهید.`}
                          className="custom-font text-xs"
                        />
                      </Form.Item>
                    </Card>
                  ))}

                  <Button
                    type="dashed"
                    onClick={() => add()}
                    block
                    icon={<PlusOutlined />}
                    className="mt-2 text-indigo-600 border-indigo-300 hover:border-indigo-500"
                  >
                    افزودن بخش
                  </Button>
                </div>
              )}
            </Form.List>

            <Form.Item
              name="is_active"
              label="وضعیت فعالیت"
              valuePropName="checked"
              className="mt-4 mb-0"
            >
              <Switch checkedChildren="فعال" unCheckedChildren="غیرفعال" />
            </Form.Item>
          </Form>
        </Modal>

        {/* مدال مشاهده کامل دانش و بخش‌ها */}
        <Modal
          title={
            <div className="flex items-center gap-2">
              <BookOutlined className="text-indigo-600" />
              <span>جزئیات دانش</span>
            </div>
          }
          open={viewModalOpen}
          onCancel={() => setViewModalOpen(false)}
          footer={[
            <Button key="close" onClick={() => setViewModalOpen(false)}>
              بستن
            </Button>,
          ]}
          width={700}
        >
          {viewKnowledge && (
            <div className="space-y-4 py-2 custom-font">
              <div className="bg-gray-50 dark:bg-gray-800 p-3 rounded-lg border border-gray-100 dark:border-gray-700 space-y-2">
                <div className="flex items-center justify-between">
                  <h3 className="text-base font-bold text-gray-800 dark:text-gray-100 m-0">
                    {viewKnowledge.title}
                  </h3>
                  <Tag color={viewKnowledge.is_active ? 'success' : 'default'}>
                    {viewKnowledge.is_active ? 'فعال' : 'غیرفعال'}
                  </Tag>
                </div>

                <div className="flex items-center gap-2 text-xs text-gray-500">
                  <Tag color="blue">
                    <FolderOutlined className="ml-1" />
                    {viewKnowledge.category?.name || 'بدون دسته‌بندی'}
                  </Tag>
                  {viewKnowledge.category?.description && (
                    <span>توضیح دسته‌بندی: {viewKnowledge.category.description}</span>
                  )}
                </div>

                {viewKnowledge.description && (
                  <p className="text-xs text-gray-600 dark:text-gray-300 m-0 border-t border-gray-200 dark:border-gray-700 pt-2">
                    {viewKnowledge.description}
                  </p>
                )}
              </div>

              <div>
                <h4 className="text-xs font-bold text-gray-700 dark:text-gray-300 mb-2 flex items-center gap-1.5">
                  <BarsOutlined className="text-indigo-600" />
                  بخش‌ها ({(viewKnowledge.parts || []).length})
                </h4>

                <div className="space-y-3">
                  {(viewKnowledge.parts || []).map((part, idx) => (
                    <Card
                      key={part.id || idx}
                      size="small"
                      className="border border-indigo-100 dark:border-indigo-900/30 shadow-xs"
                      title={
                        <div className="flex items-center justify-between">
                          <Tag color="indigo" className="m-0 font-mono text-xs">
                            بخش {idx + 1}
                          </Tag>
                        </div>
                      }
                    >
                      <Paragraph
                        className="text-xs text-gray-700 dark:text-gray-200 m-0 whitespace-pre-wrap leading-relaxed"
                      >
                        {part.content}
                      </Paragraph>
                    </Card>
                  ))}

                  {(!viewKnowledge.parts || viewKnowledge.parts.length === 0) && (
                    <Empty description="بخشی برای این دانش ثبت نشده است." />
                  )}
                </div>
              </div>
            </div>
          )}
        </Modal>
      </div>
    </MainLayout>
  );
};

export default KnowledgeBase;
